import { UIMode } from 'library/ts/enums/UIMode';
import { EventDispatcher } from 'library/ts/events/EventDispatcher';
import { IEventCallbackVOSubscriber } from 'library/ts/events/interfaces/IEventCallbackVOSubscriber';
import { IEventDispatcher } from 'library/ts/events/interfaces/IEventDispatcher';
import { Country } from 'library/ts/model/enums/geo/Country';
import { PlayerAdapterErrorCode } from 'library/ts/player/adapter/enums/PlayerAdapterErrorCode';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterErrorEventDetail } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterErrorEventDetail';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { ILogger } from 'library/ts/utilities/logger/interfaces/ILogger';
import { LogService } from 'library/ts/utilities/logger/LogService';
import { Utilities } from 'library/ts/utilities/Utilities';
import { ISmartTagRuntimeVO } from 'tag/ts/smart_tag/interfaces/ISmartTagRuntimeVO';
import { TTagRemoveReject } from 'tag/ts/smart_tag/types/api/TTagRemoveReject';
import { TTagRemoveResolve } from 'tag/ts/smart_tag/types/api/TTagRemoveResolve';
import { API } from 'tag/ts/tag/API';
import { TagController } from 'tag/ts/tag/controller/TagController';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventDataVO } from 'tag/ts/tag/events/TagEventDataVO';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { IAPI } from 'tag/ts/tag/interfaces/api/IAPI';
import { ICallbacksVO } from 'tag/ts/tag/interfaces/callbacks/ICallbacksVO';
import { IConfig } from 'tag/ts/tag/interfaces/config/IConfig';
import { IErrorManager } from 'tag/ts/tag/interfaces/error/IErrorManager';
import { ITagEventContextVO } from 'tag/ts/tag/interfaces/events/ITagEventContextVO';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { ITag } from 'tag/ts/tag/interfaces/ITag';
import { ITagController } from 'tag/ts/tag/interfaces/mvc/ITagController';
import { ITagModel } from 'tag/ts/tag/interfaces/mvc/ITagModel';
import { ITagView } from 'tag/ts/tag/interfaces/mvc/ITagView';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { ITagSnapshot } from 'tag/ts/tag/interfaces/snapshots/ITagSnapshot';
import { ITagUtilities } from 'tag/ts/tag/interfaces/utilities/ITagUtilities';
import { PerformanceMetricsManager } from 'tag/ts/tag/metrics/PerformanceMetricsManager';
import { DefaultTagConfig } from 'tag/ts/tag/model/config/DefaultTagConfig';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { TagErrorCode } from 'tag/ts/tag/model/error/TagErrorCode';
import { TagModel } from 'tag/ts/tag/model/TagModel';
import { ConfigType } from 'tag/ts/tag/model/type/ConfigType';
import { ParamsVO } from 'tag/ts/tag/model/vo/ParamsVO';
import { TTagAppEventCallback } from 'tag/ts/tag/types/TTagAppEventCallback';
import { TTagArgument } from 'tag/ts/tag/types/TTagArgument';
import { MillstoneUtilities } from 'tag/ts/tag/utilities/MillstoneUtilities';
import { platformSupported } from 'tag/ts/tag/utilities/PlatformSupport';
import { TagSnapshot } from 'tag/ts/tag/utilities/snapshots/TagSnapshot';
import { TagUtilities } from 'tag/ts/tag/utilities/TagUtilities';
import { VODFatherUtilities } from 'tag/ts/tag/utilities/VODFatherUtilities';
import { ErrorManager } from 'tag/ts/tag/view/ErrorManager';
import { TagView } from 'tag/ts/tag/view/TagView';

/**
 * @copyright © Paramount Streaming
 * @author  Stan Gletski
 * @description Tag application class
 */
export class Tag implements ITag {
	#utils: Utilities = window?.SmartTag?.utilities;
	#runtimeVO: ISmartTagRuntimeVO = window?.SmartTag?.runtimeVO;

	#uuid: string;
	#eventDispatcher: IEventDispatcher;
	#params: IParamsVO;
	#callbacks: ICallbacksVO;

	#model: ITagModel;
	#view: ITagView;
	#controller: ITagController;
	#api: IAPI;
	#errorManager: IErrorManager;
	#snapshot: ITagSnapshot;
	#logger: ILogger;
	#tagUtilities: ITagUtilities;
	#millstoneUtilities: MillstoneUtilities;
	#vodFatherUtilities: VODFatherUtilities;

	/**
	 * @description Constructor
	 * @param {TTagArgument} argument
	 */
	constructor( argument: TTagArgument ) {

		// use uuid passed via params or generate our own uuid
		this.#uuid =
			argument?.paramsVO?.TAG?.uuid
			||
			this.#utils.String.playerServicesUUID();

		this.#logger = LogService.create( { prefixes: [ 'SmartTag:', 'Tag:', this.uuid, this.name ] } );

		this.#tagUtilities = new TagUtilities( { tag: this } );
		this.#millstoneUtilities = new MillstoneUtilities( { tag: this } );
		this.#vodFatherUtilities = new VODFatherUtilities( { tag: this } );
		this.#params = new ParamsVO(); // create instance of params
		this.#callbacks = argument?.callbacksVO; // store callbacks passed from the DOM
		this.#eventDispatcher = new EventDispatcher( this?.name ); // create instance of Tag event dispatcher

		// check if params are not undefined
		if ( this.#utils.Type.isEmpty( argument?.paramsVO ) === false && this.#utils.Type.isObject( argument?.paramsVO ) ) {
			argument.paramsVO = this.tagUtilities.normalizeParams( argument?.paramsVO ); // normalize params
			this.params = this.#utils.DeepMerge.deepmerge( this.params, argument?.paramsVO ); // merge params
			this.params = this.tagUtilities.normalizeParams( this.params ); // apply defaults on merged params (e.g. SKIN.cp.enabled)
			this.params = this.tagUtilities.namespaceConverter( this.params ); // convert namespaces from string to object
			this.#logger.log( 'params [from the page]:', this.params );

			// ////////////////////////////////////////////////////////////////////////////////////////////////////////
			// process Tag container
			this.params.TAG.container = this.#utils.DOM.getElementAdvanced( this.params.TAG.container );

			// if container was found and is an html element - initialize Tag application
			if ( this.#utils.Type.isElement( this?.params?.TAG?.container ) ) {
				// Start async initialization (fire and forget - events will communicate status)
				this.#initializeAsync();
			} else {
				this.#logger.warn( 'Constructor:', 'Tag container was not found:', argument.paramsVO );
				// Dispatch fatal error event instead of calling reject callback
				this.#utils.Delay.delay().then( () => {
					this.dispatchTagEvent(
						this.getTagEventVO(
							TagEvent.ON_FATAL_ERROR,
							this,
							{
								fatalError: {
									code: TagErrorCode.CONTAINER_NOT_FOUND,
									msg: {
										original: 'Tag container was not found',
										custom: 'Error: Tag container was not found! Abort!'
									},
									error: argument.paramsVO
								}
							}
						)
					);
				} );
			}

			// ////////////////////////////////////////////////////////////////////////////////////////////////////////
		} else {
			this.#logger.warn( 'Constructor:', 'paramsVO:', argument.paramsVO );
		}
	}

	// ///////////////////////////////////////////////////////////////////////
	// ///////////////////////////////////////////////////////////////////////
	// ///////////////////////////////////////////////////////////////////////

	/**
	 * @description Async initialization entry point
	 * Coordinates the sequential initialization of all Tag components
	 * @private
	 */
	#initializeAsync = async (): Promise<void> => {
		try {
			// Wait for delay (preserving original timing)
			await this.#utils.Delay.delay();

			// Initialize core components (Model, View, API, ErrorManager) - this is synchronous
			this.#initialize();

			// Note: #initialize() calls #loadConfigs() which will eventually trigger
			// controller initialization via callback in #onTagConfigLoadSuccess()
			// The promise resolution happens via events when controller completes

		} catch ( error ) {
			this.#logger.error( 'Async initialization failed:', error );
			// Dispatch fatal error event
			this.dispatchTagEvent(
				this.getTagEventVO(
					TagEvent.ON_FATAL_ERROR,
					this,
					{
						fatalError: {
							code: TagErrorCode.INITIALIZATION_FAILURE,
							msg: {
								original: error?.message || 'Initialization failed',
								custom: this.#errorManager?.createErrorMessageByCode( TagErrorCode.INITIALIZATION_FAILURE ) || 'Tag initialization failed'
							},
							error: error
						}
					}
				)
			);
		}
	};

	/**
	 * @description This class name
	 * @returns {string}
	 */
	get name(): string {
		return 'Tag';
	}

	/**
	 * @description UUID for current Tag instance
	 * @return {string}
	 */
	get uuid(): string {
		return this.#uuid;
	}

	/**
	 * @description Smart Tag parameters
	 * @return {IParamsVO}
	 */
	get params(): IParamsVO {
		return this.#params;
	}

	set params( value: IParamsVO ) {
		this.#params = value;
	}

	get callbacks(): ICallbacksVO {
		return this.#callbacks;
	}

	set callbacks( value: ICallbacksVO ) {
		this.#callbacks = value;
	}

	/**
	 * @description Returns instance of TagUtilities class
	 * @returns {ITagUtilities}
	 */
	get tagUtilities() {
		return this.#tagUtilities;
	}

	/**
	 * @description Returns instance of MillstoneUtilities
	 * @returns {MillstoneUtilities}
	 */
	get millstoneUtilities() {
		return this.#millstoneUtilities;
	}

	/**
	 * @description Returns instance of VODFatherUtilities
	 * @returns {VODFatherUtilities}
	 */
	get vodFatherUtilities() {
		return this.#vodFatherUtilities;
	}

	/**
	 * @description Returns country code for current session
	 */
	get country(): string | Country {
		return this?.params?.CONTENT?.country || this.#runtimeVO?.top?.country || Country.US;
	}

	/**
	 * @description Returns Performance Metrics manager instance
	 */
	get performanceMetricsManager() {
		return PerformanceMetricsManager.getInstance();
	}

	/**
	 * @description Tag application level event dispatcher
	 * @returns {IEventDispatcher}
	 */
	get eventDispatcher(): IEventDispatcher {
		return this.#eventDispatcher;
	}

	/**
	 * @description Returns instance of TagEventDataVO for a tag passed as an argument
	 * @return {ITagEventDataVO}
	 */
	getTagEventDataVO = (): ITagEventDataVO => {
		return new TagEventDataVO();
	};

	/**
	 * @description Returns TagEventVO class instance
	 * @param {TagEvent} type - event type
	 * @param {ITag} tag - instance of current Tag class so whoever receives event has corresponding  Tag reference as well
	 * @param {ITagEventDataVO} data
	 * @param {*} target
	 * @param {string} targetName
	 * @return {ITagEventVO}
	 */
	getTagEventVO = ( type: TagEvent = undefined, tag: ITag = undefined, data: ITagEventDataVO = undefined, target: any = undefined, targetName: string = undefined ): ITagEventVO => {
		return new TagEventVO( type, tag, data, target, targetName || target?.name );
	};

	/**
	 *  @description Adds event listeners for all events dispatched within this Tag instance.
	 * Redirects events to a Callback function passed from a Concrete class as a parameter
	 * @param {TTagAppEventCallback} callback
	 * @param {(TagEvent | ITagEventContextVO)[]} events
	 * @param {IEventCallbackVOSubscriber} subscriber
	 */
	tagEventsHandler = ( callback: TTagAppEventCallback, events: ( TagEvent | ITagEventContextVO )[], subscriber: IEventCallbackVOSubscriber = undefined ): void => {
		if ( this.#utils.Type.isDefined( callback ) && this.#utils.Type.isDefined( events ) ) {
			// if events array is not defined subscribe to all events
			if ( this.#utils.Type.isArray( events ) ) {
				// subscribe to events
				for ( let i = 0; i < events.length; i++ ) {
					this.#eventDispatcher.addEventListener( events[ i ], callback, subscriber );
				}
			}
		}
	};

	/**
	 * @description Skin's completion hook for `tag.API.TAG.uiMode()` transitions. Called by
	 * MiniPlayerManager once an overlay swap has committed (incl. any CSS transition end);
	 * the underlying service dispatches `TagEvent.ON_UI_MODE_CHANGE_DONE` and releases the
	 * in-flight lock.
	 *
	 * Not part of the public host-facing API surface — kept off `tag.API.TAG` to signal that
	 * hosts shouldn't call this directly.
	 *
	 * @param {UIMode} mode - The mode the Skin just finished transitioning to.
	 */
	notifyUiModeChangeDone = ( mode: UIMode ): void => {
		this.#controller?.tagAPI?.notifyUiModeChangeDone( mode );
	};

	/**
	 * @description Dispatch Tag internal event with or without timeout.
	 * @param {ITagEventVO} eventVO
	 * @param {number} timeout
	 */
	dispatchTagEvent = ( eventVO: ITagEventVO | any, timeout: number = undefined ): void => {
		eventVO.uuid = this.uuid; // assign this Tag uuid to eventVO object
		// timestamp the event
		const timeNow = new Date().getTime();
		eventVO.msFromStart = timeNow - window.SmartTag.startTime;
		eventVO.timeFromStart = this.#utils.Date.secondsToHHMMSSMS( eventVO.msFromStart / 1000 );

		/**
		 * @description Executes event dispatch + callbacks
		 */
		const execute = (): void => {
			// tag event dispatched to page / external app
			if ( this.#utils.Type.isFunction( this?.params?.TAG.callback ) ) {
				this.params.TAG.callback( eventVO );
			}
			// tag event dispatched internally
			this.#eventDispatcher.dispatchEvent( eventVO );
			// tag event dispatched to skin if skin exists
			if ( this.#utils.Type.isDefined( this?.view?.skin ) && this.#utils.Type.isFunction( this?.view?.skin?.onTagEvent ) ) {
				this.view.skin.onTagEvent( eventVO );
			}
		};

		if ( isNaN( timeout ) ) {
			execute();
		} else {
			setTimeout( execute, timeout );
		}
	};

	// ///////////////////////////////////////////////////////////////////////
	// ///////////////////////////////////////////////////////////////////////
	// ///////////////////////////////////////////////////////////////////////

	/**
	 * @description Returns instance of Model for this Tag
	 */
	get model() {
		return this.#model;
	}

	/**
	 * @description Returns instance of View for this Tag
	 */
	get view() {
		return this.#view;
	}

	/**
	 * @description Returns instance of Controller for this Tag
	 */
	get controller() {
		return this.#controller;
	}

	/**
	 * @description Returns a snapshot of current player, it's state, content type
	 * <p>Prevents direct access to PlayerManager instance</p>
	 */
	get snapshot() {
		// instantiate tag snapshot
		if ( this.#utils.Type.isEmpty( this.#snapshot ) ) {
			this.#snapshot = new TagSnapshot( { tag: this } );
		}
		return this.#snapshot;
	}

	/**
	 * @description Returns instance of this Tag API
	 */
	get API() {
		return this.#api;
	}

	/**
	 * @description Returns instance of ErrorManager for this Tag
	 */
	get errorManager() {
		return this.#errorManager;
	}

	/**
	 * @description Process Error Message debug use case
	 */
	#processErrorCodeDebug = (): void => {
		this.#logger.log( 'processErrorCodeDebug:', this.#runtimeVO.debug.debugError );

		/**
		 * @description Execute error message debug use case
		 */

		// request model to load Tag configs
		this.#initTagConfig().then( ( data ) => {
			const errorCode = parseInt( this.#runtimeVO.debug.debugError );

			if ( this.#utils.Type.isNumber( errorCode ) ) {
				switch ( errorCode < 1000 ) {
					case true:
						// Error code < 1000 => dispatch Tag Fatal error event TagEvent.ON_FATAL_ERROR
						this.dispatchTagEvent(
							this.getTagEventVO(
								TagEvent.ON_FATAL_ERROR,
								this,
								{
									fatalError: {
										code: this.#runtimeVO.debug.debugError,
										msg: {
											original: undefined,
											custom: this.#errorManager.createErrorMessageByCode(
												this.#runtimeVO.debug.debugError
											)
										},
										error: data
									}
								}
							)
						);
						break;

					case false:
						// Error code >= 1000 => dispatch Tag TagEvent.ON_PLAYER_EVENT simulating VTG player Fatal error event object
						this.#eventDispatcher.dispatchEvent(
							this.getTagEventVO(
								TagEvent.ON_PLAYER_EVENT,
								this,
								{
									paramsVO: this.params,
									playerAdapterEvent: {
										type: PlayerAdapterEvent.ERROR,
										timeStamp: Date.now(),
										detail: {
											error: {
												code: this.#runtimeVO.debug.debugError as PlayerAdapterErrorCode,
												message:
													this.#errorManager.createErrorMessageByCode(
														this.#runtimeVO.debug.debugError
													),
												fatal: true,
												category: undefined,
												cause: 'TEST'
											}
										} as IPlayerAdapterErrorEventDetail,
										target: undefined
									} as IPlayerAdapterEventVO
								},
								this,
								this.name
							)
						);
						break;
				}
			} else {
				this.#logger.log( TagLoggerString.INITIALIZE, 'debugError value is invalid:', this.#runtimeVO.debug.debugError );
			}
		} );
	};

	/**
	 * @description Initialize Tag config
	 * @return {Promise<any>}
	 */
	#initTagConfig = (): Promise<any> => {
		return new Promise(
			( resolve: TTagRemoveResolve, reject: TTagRemoveReject ) => {
				// request model to load Tag configs
				this.#model.loadConfigs(
					ConfigType.TAG,
					( config: IConfig ) => {
						this.#logger.log( 'initTagConfig', 'onTagConfigLoadSuccess:', config );
						resolve( { msg: 'Tag configs loaded.' } );
					},
					( error: any ) => {
						this.#logger.log( 'onTagConfigLoadFailure:', error );
					},
					DefaultTagConfig
				);
			}
		);
	};

	/**
	 * @description Creates view
	 */
	#initialize = (): void => {
		this.#logger.log( 'initialize' );
		this.#api = new API( { tag: this } ); // create instance of API
		this.#view = new TagView( { tag: this } ); // create view
		this.#model = new TagModel( { tag: this } ); // create model
		this.#errorManager = new ErrorManager( { tag: this } );

		// pass tag to Performance Metrics Manager
		this?.performanceMetricsManager?.setTag( this );

		// Wait for skeleton creation before debug error check
		// This ensures the overlay DOM element exists when error panel needs to be displayed
		this.#utils.Delay.delay().then( () => {
			// INFO: Display error panel if debugError param is set.
			//  INFO: QA Use Case: Simulate error causing Error Panel display.
			//  INFO: Useful for debugging Error copy
			if (
				!this.#utils.Type.isEmpty( this.#runtimeVO.debug.debugError ) &&
				this.#utils.Type.isNumber( parseInt( this.#runtimeVO.debug.debugError ) )
			) {
				this.#processErrorCodeDebug(); // Process error code debug use case
				return; // IMPORTANT: EXIT
			}

			// Continue with normal initialization after skeleton is ready
			this.#continueInitialization();
		} );
	};

	/**
	 * @description Continues initialization after skeleton DOM is created
	 * @private
	 */
	#continueInitialization = (): void => {
		switch ( this?.params?.TAG?.platformSupportCheck ) {
			case true: // platform support check is enabled
				// INFO: check if platform is supported
				platformSupported( this.#runtimeVO.readyCallback )
					.then( ( data ) => {
						this.#logger.log( TagLoggerString.INITIALIZE, 'Platform is supported!', data );
						this.#loadConfigs();
					} )
					.catch( ( error ) => {
						this.#logger.warn( TagLoggerString.INITIALIZE, 'Platform is not supported!', error );

						// request model to load Tag configs
						this.#initTagConfig().then(
							( _data ) => {
								// tag fatal error event dispatched internally
								this.dispatchTagEvent(
									this.getTagEventVO(
										TagEvent.ON_FATAL_ERROR,
										this,
										{
											fatalError: {
												code: TagErrorCode.UNSUPPORTED_BROWSER,
												msg: {
													original: undefined,
													custom: this.#errorManager.createErrorMessageByCode(
														TagErrorCode.UNSUPPORTED_BROWSER
													)
												},
												error: error
											}
										}
									)
								);
							}
						);
					} );
				break;
			case false: // platform support check is disabled
				this.#logger.log( TagLoggerString.INITIALIZE, 'Platform support is disabled. Proceed regardless.' );
				this.#loadConfigs();
				break;
		}
	};

	/**
	 * @description Request model to load Tag configs skipping platform support check
	 */
	#loadConfigs = (): void => {
		this.#model.loadConfigs(
			ConfigType.TAG,
			this.#onTagConfigLoadSuccess,
			this.#onTagConfigLoadFailure,
			DefaultTagConfig
		);
	};

	/**
	 * @description On Tag configs load success
	 * @param {IConfig} config
	 */
	#onTagConfigLoadSuccess = ( config: IConfig ): void => {
		this.#logger.log( 'onTagConfigLoadSuccess:', config );
		this.#controller = new TagController( { tag: this } ); // create controller

		// Start controller initialization (async, coordinated via events)
		// Fire and forget - controller will dispatch events when ready
		if ( this.#controller.initialize ) {
			this.#controller.initialize().catch( ( error ) => {
				this.#logger.error( 'Controller initialization failed:', error );
				// Dispatch fatal error event
				this.dispatchTagEvent(
					this.getTagEventVO(
						TagEvent.ON_FATAL_ERROR,
						this,
						{
							fatalError: {
								code: TagErrorCode.INITIALIZATION_FAILURE,
								msg: {
									original: error?.message || 'Controller initialization failed',
									custom: this.#errorManager.createErrorMessageByCode( TagErrorCode.INITIALIZATION_FAILURE )
								},
								error: error
							}
						}
					)
				);
			} );
		}
	};

	/**
	 * @description On Tag configs load failure
	 * @param error
	 */
	#onTagConfigLoadFailure = ( error: any ): void => {
		this.#logger.log( 'onTagConfigLoadFailure:', error );
	};
}